import React, { useCallback, useEffect, useState } from 'react';
import InputDefault from '../../form/InputDefault';
import { IFiltroListaDemandas } from '../../../types';
import CampoComOperador from './CampoComOperador';
import AutocompleteSelect from './AutocompleteSelect';
import CheckboxSelect from './CheckboxSelect';
import ServiceSubCheckbox from './ServiceSubCheckbox';
import MultiSelectAutocomplete from './MultiSelectAutocomplete';
import {
  CampoFiltro,
  ContainerFiltrosAudiencia,
  TituloFiltrosAudiencia
} from './styles';
import api from '../../../services/api';
import { useAuth } from '../../../contexts/UserContext';

interface FiltroListaDemandasProps {
  filtros: IFiltroListaDemandas;
  atualizaFiltros: (filtro: IFiltroListaDemandas) => void;
}

export default function FiltroListaDemandas({
  filtros,
  atualizaFiltros
}: FiltroListaDemandasProps) {
  const { user } = useAuth();
  const [filtrosLocais, setFiltrosLocais] = useState<IFiltroListaDemandas>(filtros);

  // Estados para opções dinâmicas da API
  const [opcoesOperador, setOpcoesOperador] = useState<string[]>([]);
  const [opcoesComarca, setOpcoesComarca] = useState<string[]>([]);
  const [opcoesSolicitante, setOpcoesSolicitante] = useState<string[]>([]);
  const [opcoesCorrespondente, setOpcoesCorrespondente] = useState<string[]>([]);
  const [opcoesServicoComSub, setOpcoesServicoComSub] = useState<
    { value: string; label: string; subOptions?: { value: string; label: string }[] }[]
  >([]);
  const [loadingOpcoes, setLoadingOpcoes] = useState<boolean>(false);

  /** Mapeia descrição da categoria (API) para chave do filtro. */
  const categoriaParaChaveFiltro = (descricao: string) => {
    const d = (descricao || '').trim();
    if (d === 'Audiências') return 'Audiencias';
    if (d === 'Diligências') return 'Diligencias';
    if (d === 'Petição') return 'Peticoes';
    if (d === 'Protocolos') return 'Protocolos';
    if (d === 'Outros') return 'Outros';
    return d;
  };

  const categoriaParaLabelFiltro = (descricao: string) => {
    const d = (descricao || '').trim();
    if (d === 'Petição') return 'Petições';
    return d;
  };

  // Opções para filtros de audiência (mantidas estáticas)
  const opcoesModalidade = [
    { value: 'Virtual', label: 'Virtual' },
    { value: 'Presencial', label: 'Presencial' }
  ];

  const opcoesTipoAudiencia = [
    { value: 'AIJ', label: 'AIJ' },
    { value: 'Conciliação', label: 'Conciliação' },
    { value: 'Instruções e Julgamento', label: 'Instruções e Julgamento' },
    { value: 'Justificação', label: 'Justificação' },
    { value: 'Outros', label: 'Outros' },
    { value: 'Sustentação Oral', label: 'Sustentação Oral' },
    { value: 'Procon', label: 'Procon' },
    { value: 'UNA', label: 'UNA' }
  ];

  const opcoesProfissionalNecessario = [
    { value: 'Advogado', label: 'Advogado' },
    { value: 'Preposto', label: 'Preposto' },
    { value: 'Advogado + Preposto', label: 'Advogado + Preposto' }
  ];

  // Função para carregar opções da API
  const carregarOpcoes = useCallback(async () => {
    try {
      setLoadingOpcoes(true);

      // Carregar operadores das propostas
      try {
        const responseOperadores = await api.get('proposta-operadores');
        const operadores: string[] = [];
        if (responseOperadores.data?.result) {
          responseOperadores.data.result.forEach((operador: any) => {
            operadores.push(operador.nome);
          });
        }
        setOpcoesOperador(operadores);
      } catch (error) {
        console.warn('Erro ao carregar operadores:', error);
        setOpcoesOperador([]);
      }

      // Carregar correspondentes (usuários)
      const responseCorrespondentes = await api.get('usuario/options');
      const correspondentes: string[] = [];
      if (responseCorrespondentes.data?.result) {
        responseCorrespondentes.data.result.forEach((usuario: any) => {
          correspondentes.push(usuario.nome);
        });
      }
      setOpcoesCorrespondente(correspondentes);

      // Carregar comarcas
      const responseComarcas = await api.get('comarca');
      const comarcas: string[] = [];
      if (responseComarcas.data?.result) {
        responseComarcas.data.result.forEach((comarca: any) => {
          comarcas.push(`${comarca.uf} - ${comarca.cidade}`);
        });
      }
      setOpcoesComarca(comarcas);

      // Carregar solicitantes (escritórios)
      try {
        const responseSolicitantes = await api.get('escritorio');
        const solicitantes: string[] = [];
        if (responseSolicitantes.data?.result) {
          responseSolicitantes.data.result.forEach((escritorio: any) => {
            if (escritorio.nome) {
              solicitantes.push(escritorio.nome);
            }
          });
        }
        setOpcoesSolicitante(solicitantes);
      } catch (error) {
        console.warn('Erro ao carregar solicitantes:', error);
        setOpcoesSolicitante([]);
      }

      // Carregar categorias e serviços para montar opções do filtro (sem taxonomiaServicos)
      try {
        const [resCategorias, resServicos] = await Promise.all([
          api.get('servico-categoria-publico'),
          api.get('servico-publico')
        ]);
        const categorias: { id_categoria_de_servico: number; descricao: string }[] =
          resCategorias.data?.result ?? [];
        const servicos: { id_servico: number; id_categoria_de_servico: number; titulo?: string | null; nome?: string }[] =
          resServicos.data?.result ?? [];
        const subAudiencias = [
          { value: 'Audiencias|Cível', label: 'Cível' },
          { value: 'Audiencias|Outras Audiências', label: 'Outras Audiências' },
          { value: 'Audiencias|Penal', label: 'Penal' },
          { value: 'Audiencias|Trabalhista', label: 'Trabalhista' }
        ].sort((a, b) => (a.label || '').localeCompare(b.label || '', 'pt-BR'));
        const opcoes: { value: string; label: string; subOptions?: { value: string; label: string }[] }[] = [];
        for (const cat of categorias) {
          const key = categoriaParaChaveFiltro(cat.descricao);
          const label = categoriaParaLabelFiltro(cat.descricao) || cat.descricao;
          if (key === 'Audiencias') {
            opcoes.push({ value: key, label, subOptions: subAudiencias });
            continue;
          }
          const subs = servicos
            .filter(
              (s) =>
                s.id_categoria_de_servico === cat.id_categoria_de_servico &&
                (s.titulo?.trim() || (s as any).nome?.trim())
            )
            .map((s) => {
              const texto = (s.titulo ?? (s as any).nome ?? '').trim();
              // Usar identificador composto para garantir unicidade entre categorias
              return { value: `${key}|${texto}`, label: texto };
            })
            .filter((x) => x.value);
          // Ordenar sub-opções alfabeticamente em todas as categorias
          if (subs.length > 0) {
            subs.sort((a, b) => (a.label || '').localeCompare(b.label || '', 'pt-BR'));
          }
          opcoes.push({ value: key, label, subOptions: subs.length ? subs : undefined });
        }
        
        // Reordenar para que Petições fique após Diligências
        // Ordem desejada: Audiencias, Diligencias, Peticoes, Protocolos, Outros
        const ordemDesejada = ['Audiencias', 'Diligencias', 'Peticoes', 'Protocolos', 'Outros'];
        const opcoesOrdenadas = opcoes.sort((a, b) => {
          const indexA = ordemDesejada.indexOf(a.value);
          const indexB = ordemDesejada.indexOf(b.value);
          
          // Se não está na lista de ordem, coloca no final
          const posA = indexA === -1 ? 999 : indexA;
          const posB = indexB === -1 ? 999 : indexB;
          
          return posA - posB;
        });
        
        setOpcoesServicoComSub(opcoesOrdenadas);
      } catch (error) {
        console.warn('Erro ao carregar categorias/serviços:', error);
        setOpcoesServicoComSub([]);
      }

    } catch (error) {
      console.error('Erro ao carregar opções:', error);
      setOpcoesOperador([]);
      setOpcoesCorrespondente([]);
      setOpcoesComarca([]);
      setOpcoesSolicitante([]);
      setOpcoesServicoComSub([]);
    } finally {
      setLoadingOpcoes(false);
    }
  }, []);

  // Carregar opções da API quando o componente montar
  useEffect(() => {
    carregarOpcoes();
  }, [carregarOpcoes]);

  // Função utilitária para processar filtros de audiência
  // Esta função pode ser exportada e usada pelo componente pai ou API
  const processarFiltrosAudiencia = useCallback((filtros: IFiltroListaDemandas) => {
    const areasDireito = [
      'Audiencias|Cível', 'Audiencias|Penal', 'Audiencias|Trabalhista', 
      'Audiencias|Outras Audiências', 'Audiencias'
    ];
    const servicosSelecionados = filtros.servico || [];
    const temAreaDireito = servicosSelecionados.some(servico => areasDireito.includes(servico));
    const temOutrosServicos = servicosSelecionados.some(servico => !areasDireito.includes(servico));

    if (!temAreaDireito) {
      // Se não há área do direito selecionada, remove todos os filtros de audiência
      return {
        ...filtros,
        modalidade: undefined,
        tipoAudiencia: undefined,
        profissionalNecessario: undefined
      };
    }

    if (temAreaDireito && !temOutrosServicos) {
      // Se apenas áreas do direito estão selecionadas, mantém todos os filtros
      return filtros;
    }

    if (temAreaDireito && temOutrosServicos) {
      // Se áreas do direito + outros serviços estão selecionados,
      // os filtros de audiência aplicam-se apenas às áreas do direito
      // Esta lógica deve ser implementada na API/backend
      return filtros;
    }

    return filtros;
  }, []);

  // Função debounce para evitar múltiplas chamadas
  const debounce = useCallback((func: Function, wait: number) => {
    let timeout: NodeJS.Timeout;
    return function executedFunction(...args: any[]) {
      const later = () => {
        clearTimeout(timeout);
        func(...args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  }, []);

  // Função para processar valores de serviço antes de enviar para API
  // Mantém os valores como estão (com prefixo) para o backend processar
  const processarValoresServico = useCallback((filtros: IFiltroListaDemandas) => {
    // Não fazemos mais nenhuma transformação aqui
    // Os valores são enviados com prefixo para o backend
    return filtros;
  }, []);

  // Função debounced para atualizar filtros
  const atualizaFiltrosDebounced = useCallback(
    debounce((novosFiltros: IFiltroListaDemandas) => {
      // Processa os filtros de audiência antes de enviar
      const filtrosComAudiencia = processarFiltrosAudiencia(novosFiltros);
      // Remove os prefixos das categorias dos serviços
      const filtrosProcessados = processarValoresServico(filtrosComAudiencia);
      atualizaFiltros(filtrosProcessados);
    }, 500),
    [atualizaFiltros, debounce, processarFiltrosAudiencia, processarValoresServico]
  );

  // Atualiza filtros locais quando props mudam
  useEffect(() => {
    setFiltrosLocais(filtros);
  }, [filtros]);

  // Converte string monetária (ex: "R$ 1.234,56") para número (1234.56)
  const parseCurrencyToNumber = useCallback((input?: string): number | undefined => {
    if (!input) return undefined;
    const numbersOnly = input.replace(/\D/g, '');
    if (!numbersOnly) return undefined;
    return parseInt(numbersOnly, 10) / 100;
  }, []);

  // Função para atualizar um campo específico dos filtros
  const atualizarCampoFiltro = useCallback((campo: keyof IFiltroListaDemandas, valor: any) => {
    let novosFiltros = { ...filtrosLocais, [campo]: valor };
    
    // Se o campo sendo atualizado é 'servico' e nenhuma área do direito está selecionada,
    // limpa os filtros específicos de audiência
    if (campo === 'servico') {
      const areasDireito = [
        'Audiencias|Cível', 'Audiencias|Penal', 'Audiencias|Trabalhista', 
        'Audiencias|Outras Audiências', 'Audiencias'
      ];
      const temAreaDireito = valor?.some((servico: string) => areasDireito.includes(servico));
      
      if (!temAreaDireito) {
        novosFiltros = {
          ...novosFiltros,
          modalidade: undefined,
          tipoAudiencia: undefined,
          profissionalNecessario: undefined
        };
      }
    }
    
    setFiltrosLocais(novosFiltros);
    atualizaFiltrosDebounced(novosFiltros);
  }, [filtrosLocais, atualizaFiltrosDebounced]);

  // Verifica se qualquer área do direito está selecionada
  const isAudienciasSelecionado = useCallback(() => {
    const areasDireito = [
      'Audiencias|Cível', 'Audiencias|Penal', 'Audiencias|Trabalhista', 
      'Audiencias|Outras Audiências', 'Audiencias'
    ];
    return filtrosLocais.servico?.some(servico => areasDireito.includes(servico)) || false;
  }, [filtrosLocais.servico]);

  // Função para atualizar campos com operadores
  const atualizarCampoComOperador = useCallback((campo: keyof IFiltroListaDemandas, operador: 'igual' | 'maior' | 'menor' | 'entre', valor?: string, valorMin?: string, valorMax?: string) => {
    let novosDados: any = { operador };

    if (campo === 'valorCliente') {
      if (operador === 'entre') {
        novosDados.valorMin = parseCurrencyToNumber(valorMin);
        novosDados.valorMax = parseCurrencyToNumber(valorMax);
      } else {
        novosDados.valor = parseCurrencyToNumber(valor);
      }
    } else {
      if (operador === 'entre') {
        novosDados.dataInicio = valorMin ? new Date(valorMin) : undefined;
        novosDados.dataFim = valorMax ? new Date(valorMax) : undefined;
      } else {
        novosDados.data = valor ? new Date(valor) : undefined;
      }
    }

    const novosFiltros = {
      ...filtrosLocais,
      [campo]: novosDados
    };

    setFiltrosLocais(novosFiltros);
    atualizaFiltrosDebounced(novosFiltros);
  }, [filtrosLocais, atualizaFiltrosDebounced, parseCurrencyToNumber]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
      {/* Campos conforme a imagem */}
      <CampoFiltro>
        <InputDefault
          title="ID"
          placeholder="Digite o ID ou # + ID"
          value={filtrosLocais.id || ''}
          onChangeText={(valor) => atualizarCampoFiltro('id', valor)}
        />
      </CampoFiltro>

      <CampoFiltro>
        <InputDefault
          title="NÚMERO DO PROCESSO/IDENTIFICAÇÃO"
          placeholder="Digite o número do processo ou identifica"
          value={filtrosLocais.numeroProcesso || ''}
          onChangeText={(valor) => atualizarCampoFiltro('numeroProcesso', valor)}
        />
      </CampoFiltro>

      {
        user.profile !== 'comum' && user.profile !== 'Solicitante' &&
      <CampoFiltro>
        <AutocompleteSelect
          titulo="OPERADOR"
          placeholder={loadingOpcoes ? "Carregando operadores..." : "Selecione o operador"}
          value={filtrosLocais.operador || ''}
          onChange={(valor) => atualizarCampoFiltro('operador', valor)}
          options={opcoesOperador}
        />
      </CampoFiltro>
      }

      {
        user.profile !== 'comum' && user.profile !== 'Solicitante' &&
      <CampoFiltro>
        <AutocompleteSelect
          titulo="SOLICITANTE"
          placeholder={loadingOpcoes ? "Carregando solicitantes..." : "Selecione o solicitante"}
          value={filtrosLocais.solicitante || ''}
          onChange={(valor) => atualizarCampoFiltro('solicitante', valor)}
          options={opcoesSolicitante}
        />
      </CampoFiltro>
      }

      {
        user.profile !== 'comum' && 
      <CampoFiltro>
        <AutocompleteSelect
          titulo="CORRESPONDENTE"
          placeholder={loadingOpcoes ? "Carregando correspondentes..." : "Selecione o correspondente"}
          value={filtrosLocais.correspondente || ''}
          onChange={(valor) => atualizarCampoFiltro('correspondente', valor)}
          options={opcoesCorrespondente}
        />
      </CampoFiltro>
      }

      <CampoFiltro>
        <MultiSelectAutocomplete
          titulo="COMARCA"
          placeholder={loadingOpcoes ? "Carregando comarcas..." : "Selecione as comarcas"}
          value={filtrosLocais.comarca || []}
          onChange={(valores) => atualizarCampoFiltro('comarca', valores)}
          options={opcoesComarca}
        />
      </CampoFiltro>

      <CampoFiltro>
        <ServiceSubCheckbox
          titulo="SERVIÇO"
          placeholder={loadingOpcoes ? "Carregando serviços..." : "Selecione os serviços"}
          value={filtrosLocais.servico || []}
          onChange={(valores) => atualizarCampoFiltro('servico', valores)}
          options={opcoesServicoComSub}
        />
      </CampoFiltro>

      {/* Filtros específicos para Audiências - aparecem quando qualquer área do direito está selecionada */}
      {isAudienciasSelecionado() && (
        <ContainerFiltrosAudiencia>
          <TituloFiltrosAudiencia>FILTROS DE AUDIÊNCIA</TituloFiltrosAudiencia>
          
          <CampoFiltro>
            <CheckboxSelect
              titulo="MODALIDADE"
              placeholder="Selecione as modalidades"
              value={filtrosLocais.modalidade || []}
              onChange={(valores) => atualizarCampoFiltro('modalidade', valores)}
              options={opcoesModalidade}
            />
          </CampoFiltro>

          <CampoFiltro>
            <CheckboxSelect
              titulo="TIPO DE AUDIÊNCIA"
              placeholder="Selecione os tipos de audiência"
              value={filtrosLocais.tipoAudiencia || []}
              onChange={(valores) => atualizarCampoFiltro('tipoAudiencia', valores)}
              options={opcoesTipoAudiencia}
            />
          </CampoFiltro>

          <CampoFiltro>
            <CheckboxSelect
              titulo="PROFISSIONAL NECESSÁRIO"
              placeholder="Selecione os profissionais"
              value={filtrosLocais.profissionalNecessario || []}
              onChange={(valores) => atualizarCampoFiltro('profissionalNecessario', valores)}
              options={opcoesProfissionalNecessario}
            />
          </CampoFiltro>
        </ContainerFiltrosAudiencia>
      )}

      <CampoFiltro>
        <InputDefault
          title="PARTE ENVOLVIDA"
          placeholder="Digite a parte envolvida"
          value={filtrosLocais.parteEnvolvida || ''}
          onChangeText={(valor) => atualizarCampoFiltro('parteEnvolvida', valor)}
        />
      </CampoFiltro>

      <CampoFiltro>
        <InputDefault
          title="PARTE REPRESENTADA"
          placeholder="Digite a parte representada"
          value={filtrosLocais.parteRepresentada || ''}
          onChangeText={(valor) => atualizarCampoFiltro('parteRepresentada', valor)}
        />
      </CampoFiltro>

      {/* Campos com operadores conforme segunda imagem */}
      {
        user.profile !== 'comum' && user.profile !== 'Solicitante' &&
      <CampoComOperador
        titulo="VALOR DO CLIENTE"
        placeholder="Digite o valor"
        operador={filtrosLocais.valorCliente?.operador || 'igual'}
        valor={filtrosLocais.valorCliente?.valor}
        valorMin={filtrosLocais.valorCliente?.valorMin}
        valorMax={filtrosLocais.valorCliente?.valorMax}
        tipo="number"
        onOperadorChange={(operador) => atualizarCampoComOperador('valorCliente', operador)}
        onValorChange={(valor) => atualizarCampoComOperador('valorCliente', filtrosLocais.valorCliente?.operador || 'igual', valor)}
        onValorMinChange={(valor) => atualizarCampoComOperador('valorCliente', 'entre', undefined, valor, filtrosLocais.valorCliente?.valorMax?.toString())}
        onValorMaxChange={(valor) => atualizarCampoComOperador('valorCliente', 'entre', undefined, filtrosLocais.valorCliente?.valorMin?.toString(), valor)}
      />
      }

      <CampoComOperador
        titulo="PRAZO FATAL"
        placeholder="Selecione a data"
        operador={filtrosLocais.prazoFatal?.operador || 'igual'}
        valor={filtrosLocais.prazoFatal?.data?.toISOString().split('T')[0]}
        valorMin={filtrosLocais.prazoFatal?.dataInicio?.toISOString().split('T')[0]}
        valorMax={filtrosLocais.prazoFatal?.dataFim?.toISOString().split('T')[0]}
        tipo="date"
        onOperadorChange={(operador) => atualizarCampoComOperador('prazoFatal', operador)}
        onValorChange={(valor) => atualizarCampoComOperador('prazoFatal', filtrosLocais.prazoFatal?.operador || 'igual', valor)}
        onValorMinChange={(valor) => atualizarCampoComOperador('prazoFatal', 'entre', undefined, valor, filtrosLocais.prazoFatal?.dataFim?.toISOString().split('T')[0])}
        onValorMaxChange={(valor) => atualizarCampoComOperador('prazoFatal', 'entre', undefined, filtrosLocais.prazoFatal?.dataInicio?.toISOString().split('T')[0], valor)}
      />

      <CampoComOperador
        titulo="CRIADO EM"
        placeholder="Selecione a data"
        operador={filtrosLocais.solicitadaEm?.operador || 'igual'}
        valor={filtrosLocais.solicitadaEm?.data?.toISOString().split('T')[0]}
        valorMin={filtrosLocais.solicitadaEm?.dataInicio?.toISOString().split('T')[0]}
        valorMax={filtrosLocais.solicitadaEm?.dataFim?.toISOString().split('T')[0]}
        tipo="date"
        onOperadorChange={(operador) => atualizarCampoComOperador('solicitadaEm', operador)}
        onValorChange={(valor) => atualizarCampoComOperador('solicitadaEm', filtrosLocais.solicitadaEm?.operador || 'igual', valor)}
        onValorMinChange={(valor) => atualizarCampoComOperador('solicitadaEm', 'entre', undefined, valor, filtrosLocais.solicitadaEm?.dataFim?.toISOString().split('T')[0])}
        onValorMaxChange={(valor) => atualizarCampoComOperador('solicitadaEm', 'entre', undefined, filtrosLocais.solicitadaEm?.dataInicio?.toISOString().split('T')[0], valor)}
      />

      <CampoComOperador
        titulo="PRAZO PARA ACEITE"
        placeholder="Selecione a data"
        operador={filtrosLocais.prazoParaAceite?.operador || 'igual'}
        valor={filtrosLocais.prazoParaAceite?.data?.toISOString().split('T')[0]}
        valorMin={filtrosLocais.prazoParaAceite?.dataInicio?.toISOString().split('T')[0]}
        valorMax={filtrosLocais.prazoParaAceite?.dataFim?.toISOString().split('T')[0]}
        tipo="date"
        onOperadorChange={(operador) => atualizarCampoComOperador('prazoParaAceite', operador)}
        onValorChange={(valor) => atualizarCampoComOperador('prazoParaAceite', filtrosLocais.prazoParaAceite?.operador || 'igual', valor)}
        onValorMinChange={(valor) => atualizarCampoComOperador('prazoParaAceite', 'entre', undefined, valor, filtrosLocais.prazoParaAceite?.dataFim?.toISOString().split('T')[0])}
        onValorMaxChange={(valor) => atualizarCampoComOperador('prazoParaAceite', 'entre', undefined, filtrosLocais.prazoParaAceite?.dataInicio?.toISOString().split('T')[0], valor)}
      />

      <CampoComOperador
        titulo="INICIADA EM"
        placeholder="Selecione a data"
        operador={filtrosLocais.iniciadaEm?.operador || 'igual'}
        valor={filtrosLocais.iniciadaEm?.data?.toISOString().split('T')[0]}
        valorMin={filtrosLocais.iniciadaEm?.dataInicio?.toISOString().split('T')[0]}
        valorMax={filtrosLocais.iniciadaEm?.dataFim?.toISOString().split('T')[0]}
        tipo="date"
        onOperadorChange={(operador) => atualizarCampoComOperador('iniciadaEm', operador)}
        onValorChange={(valor) => atualizarCampoComOperador('iniciadaEm', filtrosLocais.iniciadaEm?.operador || 'igual', valor)}
        onValorMinChange={(valor) => atualizarCampoComOperador('iniciadaEm', 'entre', undefined, valor, filtrosLocais.iniciadaEm?.dataFim?.toISOString().split('T')[0])}
        onValorMaxChange={(valor) => atualizarCampoComOperador('iniciadaEm', 'entre', undefined, filtrosLocais.iniciadaEm?.dataInicio?.toISOString().split('T')[0], valor)}
      />

      <CampoComOperador
        titulo="PRAZO PARA CONCLUSÃO"
        placeholder="Selecione a data"
        operador={filtrosLocais.prazoParaConclusao?.operador || 'igual'}
        valor={filtrosLocais.prazoParaConclusao?.data?.toISOString().split('T')[0]}
        valorMin={filtrosLocais.prazoParaConclusao?.dataInicio?.toISOString().split('T')[0]}
        valorMax={filtrosLocais.prazoParaConclusao?.dataFim?.toISOString().split('T')[0]}
        tipo="date"
        onOperadorChange={(operador) => atualizarCampoComOperador('prazoParaConclusao', operador)}
        onValorChange={(valor) => atualizarCampoComOperador('prazoParaConclusao', filtrosLocais.prazoParaConclusao?.operador || 'igual', valor)}
        onValorMinChange={(valor) => atualizarCampoComOperador('prazoParaConclusao', 'entre', undefined, valor, filtrosLocais.prazoParaConclusao?.dataFim?.toISOString().split('T')[0])}
        onValorMaxChange={(valor) => atualizarCampoComOperador('prazoParaConclusao', 'entre', undefined, filtrosLocais.prazoParaConclusao?.dataInicio?.toISOString().split('T')[0], valor)}
      />
    </div>
  );
};

